import { observer } from 'mobx-react-lite'
import { type BookkeepingProgressionViewModel } from '@/features/jobs/details/components/BookkeepingProgressionCard/BookkeepingProgressionViewModel'
import { match } from '@/utils/pattern-match-util'
import {
  formatShortDate,
  formatCycleToShortDate,
  formatBookkeepingCycleStatus,
} from '@/features/jobs/bookkeeping/bookkeeping-cycle-formatters'
import { EmptyState, EmptyStateDescription } from '@/components/EmptyState'
import { getBookkeepingCycleCompletedAt } from '@/features/jobs/bookkeeping/BookkeepingProgressionInfo'
import {
  BookkeepingCycleItem,
  BookkeepingCyclesList,
  BookkeepingFinishedItemsCard,
  BookkeepingFinishedItemsList,
  BookkeepingTrackerCard,
} from '@/features/jobs/details/components/BookkeepingTrackerCard'

export interface BookkeepingTrackerCardContainerProps {
  vm: BookkeepingProgressionViewModel
}

export const BookkeepingTrackerCardContainer = observer(
  function BookkeepingTrackerCardContainer({
    vm,
  }: BookkeepingTrackerCardContainerProps) {
    if (!vm.canViewBookkeepingCyclesTab) {
      return (
        <BookkeepingFinishedItemsCard>
          <FinishedItemsListContainer vm={vm} />
        </BookkeepingFinishedItemsCard>
      )
    }

    return (
      <BookkeepingTrackerCard
        activeTab={vm.bookkeepingTrackerCardTabController.current.tab}
        onTabChange={vm.bookkeepingTrackerCardTabController.change}
      >
        {match(vm.bookkeepingTrackerCardTabController.current.tab, {
          FinishedItems: () => (
            <FinishedItemsListContainer vm={vm} topPadding />
          ),
          Cycles: vm.anyCycles
            ? () => <CyclesListContainer vm={vm} />
            : () => (
                <EmptyState size="card" padTop>
                  <EmptyStateDescription>
                    No cycles available.
                  </EmptyStateDescription>
                </EmptyState>
              ),
        })}
      </BookkeepingTrackerCard>
    )
  },
)

interface FinishedItemsListContainerProps {
  vm: BookkeepingProgressionViewModel
  topPadding?: boolean
}

const FinishedItemsListContainer = observer(
  function FinishedItemsListContainer({
    vm,
    topPadding,
  }: FinishedItemsListContainerProps) {
    return (
      <BookkeepingFinishedItemsList
        topPadding={topPadding}
        items={vm.workItems?.map((wi) => ({
          id: wi.workItemId,
          name: wi.inventoryItemName,
          completedCount: wi.completedCount,
          quantity: wi.quantity,
        }))}
      />
    )
  },
)

interface CyclesListContainerProps {
  vm: BookkeepingProgressionViewModel
}

const CyclesListContainer = observer(function CyclesListContainer({
  vm,
}: CyclesListContainerProps) {
  return (
    <BookkeepingCyclesList>
      {vm.cyclesNewestFirst.map((cycle) => (
        <BookkeepingCycleItem
          key={cycle.id}
          statusName={formatBookkeepingCycleStatus(cycle.status)}
          number={cycle.number}
          date={
            getBookkeepingCycleCompletedAt(cycle)
              ? formatCycleToShortDate(cycle)
              : formatShortDate(cycle.startedAt)
          }
          onView={() => vm.bookkeepingCycleDetailsVm.open(cycle)}
        />
      ))}
    </BookkeepingCyclesList>
  )
})
